/**
 * ============================================================================
 *   Copyright (c) 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * ============================================================================
 *   Module       : StatCard
 *   Feature      : 197-portal-light-theme-responsive (design blueprint)
 *   Description  : Canonical KPI / stat tile (label + big value + optional
 *                  subtext / icon / link). Replaces the many ad-hoc
 *                  `bg-white … rounded … <div>` stat blocks on the dashboards.
 *   Blueprint    : docs/design-blueprint.md
 *   Constitution : v1.19.0
 *   Author       : Peter Dobler
 * ============================================================================
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { Sparkline } from '../Dashboard/panels/Sparkline';
import { AppTooltip } from './AppTooltip';

export interface StatCardProps {
  label: React.ReactNode;
  value: React.ReactNode;
  subtext?: React.ReactNode;
  icon?: React.ReactNode;
  /** Tailwind text-color class for the value (e.g. status colors). */
  valueClassName?: string;
  /** Render the whole tile as a link. */
  to?: string;
  className?: string;
  /**
   * Spec 215 US4: optional 60-day daily series rendered as a sparkline under the
   * value. Suppressed (no layout shift) when absent or <2 points (Sparkline contract).
   */
  trend?: Array<{ date: string; value: number }>;
  /** Tooltip value formatter for the trend sparkline. */
  trendValueFmt?: (n: number) => string;
  /** Tooltip series label for the trend sparkline. */
  trendLabel?: string;
  /** Optional hover explanation of what the tile means (wraps the tile in an AppTooltip). */
  tooltip?: React.ReactNode;
}

/** Canonical KPI tile. */
export const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  subtext,
  icon,
  valueClassName,
  to,
  className,
  trend,
  trendValueFmt,
  trendLabel,
  tooltip,
}) => {
  const body = (
    <>
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-[var(--color-text-secondary)]">{label}</div>
        {icon && <span className="text-[var(--color-text-secondary)]">{icon}</span>}
      </div>
      {/* Use valueClassName's color when provided — DON'T also emit the base
          text-primary, or the two same-specificity arbitrary color utilities
          conflict and the override is silently dropped (CSS source order wins). */}
      <div className={clsx('mt-1 text-2xl font-bold', valueClassName || 'text-[var(--color-text-primary)]')}>
        {value}
      </div>

      {subtext != null && <div className="mt-0.5 text-xs text-[var(--color-text-secondary)]">{subtext}</div>}
      {trend && <Sparkline data={trend} valueFmt={trendValueFmt} label={trendLabel} />}
    </>
  );

  const base = clsx(
    'block rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5 shadow-[var(--shadow-md)]',
    to && 'transition-shadow hover:shadow-[var(--shadow-lg)]',
    className,
  );

  const card = to ? <Link to={to} className={base}>{body}</Link> : <div className={base}>{body}</div>;
  return tooltip != null ? (
    <AppTooltip title={tooltip} placement="top" className="block">
      <div>{card}</div>
    </AppTooltip>
  ) : card;
};

export default StatCard;
